// ─── 🤖 Impostazioni → Fasce di punta, e Duet ────────────────────────────────
//
// Mockup «Pacchetto 3 · La porta per Duet» v4.
//   · 🕐 **Fasce di punta** (1A): le quattro fasce del bot per ogni giorno della settimana, una casella che
//     si tocca e gira fra «normale», «di punta», «tranquilla». Nel weekend il bot offre solo mattina e
//     pomeriggio: le altre due si vedono spente.
//   · 🔓 **Duet**: le maglie, tipo per tipo («chiede a me» o «manda da sola»), e chi approva (6A: solo
//     l'amministrazione). Chi approva riceve le proposte su Telegram coi bottoni (7A): serve la scheda
//     collegata al bot.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ETICHETTE_TIPO_DI_FASCIA,
  ETICHETTE_TIPO_DI_PROPOSTA,
  FASCE_DEL_GIORNO,
  FASCE_DEL_WEEKEND,
  GIORNI_DELLA_SETTIMANA,
  PAROLE_FASCE,
  TIPI_DI_FASCIA,
  type ElencoSoci,
  type FasciaDiPunta,
  type ModoDelleMaglie,
  type TipoDiFascia,
  type TipoDiProposta,
} from '@pmo/shared';
import { chiama } from '../../lib/api.js';
import { Attesa, Campo, Guasto } from '../../components/pezzi.js';
import { SoloLettura, usaModifica } from './comune.js';

// ── 🕐 Le fasce di punta (1A) ────────────────────────────────────────────────

const chiave = (f: { weekday: number; band: string }) => `${f.weekday}~${f.band}`;
const nelWeekend = (weekday: number) => weekday >= 6;

export function FasceDiPunta(): React.JSX.Element {
  const puòModificare = usaModifica('manage_settings');
  const qc = useQueryClient();
  const dati = useQuery({
    queryKey: ['fasce-di-punta'],
    queryFn: () => chiama<{ items: FasciaDiPunta[] }>('GET', '/settings/peak-bands'),
  });
  const [cambiate, setCambiate] = useState<Map<string, TipoDiFascia>>(new Map());
  const salva = useMutation({
    meta: { fatto: 'fasce di punta salvate' },
    mutationFn: (items: FasciaDiPunta[]) =>
      chiama<{ items: FasciaDiPunta[] }>('PUT', '/settings/peak-bands', { items }),
    onSuccess: (d) => {
      qc.setQueryData(['fasce-di-punta'], d);
      setCambiate(new Map());
    },
  });

  if (dati.isPending) return <Attesa cosa="le fasce di punta" />;
  if (dati.error) return <Guasto errore={dati.error} />;
  const di = new Map(dati.data.items.map((f) => [chiave(f), f.kind]));
  const vale = (weekday: number, band: string): TipoDiFascia =>
    cambiate.get(`${weekday}~${band}`) ?? di.get(`${weekday}~${band}`) ?? 'normale';

  return (
    <section className="fasce-di-punta">
      <header className="testata">
        <h2>Fasce di punta</h2>
        <p className="tenue piccolo">
          Com’è ogni fascia della settimana. Duet le usa per dire quali buchi pesano di più: un’ora libera il
          martedì sera vale più di una il martedì a pranzo. Le fasce sono quelle del bot; finché nessuno le
          segna, sono tutte «normale». Si tocca una casella per cambiarla.
        </p>
        <SoloLettura mostra={puòModificare} />
      </header>
      <div className="tabella-scorre">
        <table className="griglia-punta">
          <thead>
            <tr>
              <th />
              {GIORNI_DELLA_SETTIMANA.map((g) => (
                <th key={g}>{g}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {FASCE_DEL_GIORNO.map((band) => (
              <tr key={band}>
                <th>{PAROLE_FASCE[band]}</th>
                {GIORNI_DELLA_SETTIMANA.map((_, i) => {
                  const weekday = i + 1;
                  const spenta =
                    nelWeekend(weekday) && !(FASCE_DEL_WEEKEND as readonly string[]).includes(band);
                  const k = vale(weekday, band);
                  return (
                    <td key={weekday}>
                      <button
                        type="button"
                        className={`casella-punta ${spenta ? 'spenta' : k}`}
                        disabled={!puòModificare || spenta}
                        title={spenta ? 'Il bot non la offre nel weekend' : undefined}
                        onClick={() => {
                          const dopo =
                            TIPI_DI_FASCIA[(TIPI_DI_FASCIA.indexOf(k) + 1) % TIPI_DI_FASCIA.length]!;
                          setCambiate(new Map(cambiate).set(`${weekday}~${band}`, dopo));
                        }}
                      >
                        {spenta ? '—' : ETICHETTE_TIPO_DI_FASCIA[k]}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Guasto errore={salva.error} />
      {puòModificare && (
        <div className="riga-bottoni">
          <button
            type="button"
            disabled={cambiate.size === 0 || salva.isPending}
            onClick={() =>
              salva.mutate(
                [...cambiate].map(([k, kind]) => {
                  const [w, band] = k.split('~');
                  return { weekday: Number(w), band: band as FasciaDiPunta['band'], kind };
                }),
              )
            }
          >
            {salva.isPending ? 'Un attimo…' : 'Salva le fasce'}
          </button>
          {cambiate.size > 0 && <span className="tenue">modifiche da salvare: {cambiate.size}</span>}
        </div>
      )}
    </section>
  );
}

// ── 🔓 Duet: le maglie e chi approva ─────────────────────────────────────────

interface Impostazioni {
  maglie: { items: Array<{ kind: TipoDiProposta; mode: ModoDelleMaglie }> };
  approvatori: { items: Array<{ memberId: string; nome: string; telegram: boolean }> };
}

const ESEMPI: Record<TipoDiProposta, string> = {
  risposta: '«Venerdì sera si è liberato il Campo 2…»',
  informativa: '«Da lunedì il circolo apre alle 8»',
  disponibilita: '«Quando ti farebbe piacere giocare?»',
  invito: '«La partita di Luca B. cerca un giocatore»',
  domanda_disdetta: '«Ti va di dirci perché?»',
};

export function DuetImpostazioni(): React.JSX.Element {
  const puòModificare = usaModifica('manage_users');
  const qc = useQueryClient();
  const dati = useQuery({
    queryKey: ['duet-impostazioni'],
    queryFn: () => chiama<Impostazioni>('GET', '/settings/duet'),
  });
  const metti = (d: Impostazioni) => qc.setQueryData(['duet-impostazioni'], d);
  const maglia = useMutation({
    meta: { fatto: 'maglie salvate' },
    mutationFn: (m: { kind: TipoDiProposta; mode: ModoDelleMaglie }) =>
      chiama<Impostazioni>('PUT', '/settings/duet/maglie', { items: [m] }),
    onSuccess: metti,
  });
  const aggiungi = useMutation({
    meta: { fatto: 'aggiunto fra chi approva' },
    mutationFn: (memberId: string) => chiama<Impostazioni>('POST', '/settings/duet/approvers', { memberId }),
    onSuccess: metti,
  });
  const togli = useMutation({
    meta: { fatto: 'tolto da chi approva' },
    mutationFn: (memberId: string) =>
      chiama<Impostazioni>('POST', `/settings/duet/approvers/${memberId}/remove`),
    onSuccess: metti,
  });
  const [q, setQ] = useState('');
  const soci = useQuery({
    queryKey: ['duet-cerca', q],
    queryFn: () =>
      chiama<ElencoSoci>('GET', `/members?${new URLSearchParams({ q: q.trim(), limit: '8' }).toString()}`),
    enabled: q.trim().length >= 2,
  });

  if (dati.isPending) return <Attesa cosa="le impostazioni di Duet" />;
  if (dati.error) return <Guasto errore={dati.error} />;
  const { maglie, approvatori } = dati.data;
  const gia = new Set(approvatori.items.map((a) => a.memberId));

  return (
    <section className="duet-impostazioni">
      <header className="testata">
        <h2>Duet</h2>
        <p className="tenue piccolo">
          Duet legge i dati del circolo e propone messaggi ai soci. Fra quello che propone e il socio c’è chi
          approva: le proposte arrivano in Analytics → Proposte di Duet e nel suo Telegram, con i bottoni ✅
          ✏️ ❌.
        </p>
        <SoloLettura mostra={puòModificare} />
      </header>

      <h3 className="titolo-sezione">Le maglie</h3>
      <p className="tenue piccolo">
        Per ogni tipo di messaggio: Duet chiede, oppure manda da sola. Anche a maglie larghe valgono le regole
        di sempre: un messaggio di Duet al giorno per socio, frasi cortesi, niente soldi, niente regole
        cambiate. Quello che manda da sola si ritrova fra le proposte, segnato «mandata da sola».
      </p>
      <div className="tabella-scorre">
        <table>
          <thead>
            <tr>
              <th>Tipo di messaggio</th>
              <th>Esempio</th>
              <th>Duet</th>
            </tr>
          </thead>
          <tbody>
            {maglie.items.map((m) => (
              <tr key={m.kind}>
                <td>{ETICHETTE_TIPO_DI_PROPOSTA[m.kind]}</td>
                <td className="tenue">{ESEMPI[m.kind]}</td>
                <td>
                  <select
                    value={m.mode}
                    disabled={!puòModificare || maglia.isPending}
                    onChange={(e) => maglia.mutate({ kind: m.kind, mode: e.target.value as ModoDelleMaglie })}
                  >
                    <option value="chiede">✋ chiede a me</option>
                    <option value="sola">🔓 manda da sola</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Guasto errore={maglia.error} />

      <h3 className="titolo-sezione">Chi approva</h3>
      <p className="tenue piccolo">
        Chi approva riceve ogni proposta nel suo Telegram, e può parlare con Duet dal bot («🤖 Parla con
        Duet»). Serve la scheda socio collegata al bot.
      </p>
      {approvatori.items.length === 0 ? (
        <p className="tenue">Ancora nessuno: le proposte si approvano solo da qui.</p>
      ) : (
        <ul className="storico">
          {approvatori.items.map((a) => (
            <li key={a.memberId}>
              <strong>{a.nome}</strong>{' '}
              <span className="tenue">
                {a.telegram ? '· collegato al bot' : '· la scheda non è collegata al bot'}
              </span>{' '}
              {puòModificare && (
                <button
                  type="button"
                  className="bordato piccolo"
                  disabled={togli.isPending}
                  onClick={() => togli.mutate(a.memberId)}
                >
                  Togli
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <Guasto errore={aggiungi.error ?? togli.error} />
      {puòModificare && (
        <div className="riquadro">
          <Campo
            etichetta="Aggiungi: cerca il socio in anagrafica"
            placeholder="cognome o nome"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <ul className="storico">
            {(soci.data?.items ?? [])
              .filter((s) => !gia.has(s.id))
              .map((s) => (
                <li key={s.id}>
                  {s.displayName}{' '}
                  <button
                    type="button"
                    className="bordato piccolo"
                    disabled={aggiungi.isPending}
                    onClick={() => {
                      aggiungi.mutate(s.id);
                      setQ('');
                    }}
                  >
                    Aggiungi
                  </button>
                </li>
              ))}
          </ul>
        </div>
      )}
    </section>
  );
}
